import {
  useEffect,
  useState,
  useCallback,
  useSyncExternalStore,
  useRef,
  useMemo,
} from "react";
import { OfflineQueue, RequestProcessedEvent } from "@/services/offlineQueue";
import { useQueryClient } from "@tanstack/react-query";
import { NetworkDetector } from "@/services/networkDetector";
import { parseRequestUrl } from "@/lib/utils/parseRequestUrl";
import { updateCacheForModule } from "@/lib/utils/updateCacheForModule";

export interface OfflineSyncState {
  queueSize: number;
  isProcessing: boolean;
  lastSyncTime: Date | null;
}

interface OfflineSyncReturn extends OfflineSyncState {
  isOnline: boolean;
  processQueue: () => Promise<void>;
}

/**
 * Hook for managing offline synchronization state and processing queued requests.
 *
 * This hook provides:
 * 1. Real-time online/offline state tracking (directly from NetworkDetector)
 * 2. Offline queue size monitoring with automatic verification
 * 3. Manual queue processing functionality
 * 4. Automatic cache invalidation for successfully processed offline requests
 */
export function useOfflineSync(): OfflineSyncReturn {
  const queryClient = useQueryClient();
  const isProcessingRef = useRef(false);
  const syncedModulesRef = useRef<Set<string>>(new Set());

  const [offlineSyncState, setOfflineSyncState] = useState<OfflineSyncState>({
    queueSize: 0,
    isProcessing: false,
    lastSyncTime: null,
  });

  // Use useSyncExternalStore for network status subscription
  const isOnline = useSyncExternalStore(
    (callback) => {
      NetworkDetector.on("status-change", callback);
      return () => NetworkDetector.off("status-change", callback);
    },
    () => NetworkDetector.getStatus().isOnline,
    () => true, // Server-side rendering fallback
  );

  const updateQueueSize = useCallback(async () => {
    try {
      const size = await OfflineQueue.getQueueSize();
      setOfflineSyncState((prev) => ({ ...prev, queueSize: size }));
    } catch (error) {
      console.error("Failed to get queue size:", error);
      // On error, assume queue is empty to avoid showing incorrect counts
      setOfflineSyncState((prev) => ({ ...prev, queueSize: 0 }));
    }
  }, []);

  const processQueue = useCallback(async () => {
    // Use ref to avoid race conditions
    if (!isOnline || isProcessingRef.current) return;

    isProcessingRef.current = true;
    setOfflineSyncState((prev) => ({ ...prev, isProcessing: true }));
    syncedModulesRef.current.clear();

    try {
      await OfflineQueue.processQueue();
      await updateQueueSize();

      // Invalidate only the modules that were synced
      if (syncedModulesRef.current.size > 0) {
        syncedModulesRef.current.forEach((moduleKey) => {
          queryClient.invalidateQueries({ queryKey: [moduleKey] });
        });
      } else {
        // Fallback: if no specific modules tracked, invalidate all
        queryClient.invalidateQueries();
      }

      setOfflineSyncState((prev) => ({
        ...prev,
        isProcessing: false,
        lastSyncTime: new Date(),
      }));
    } catch (error) {
      console.error("Failed to process offline queue:", error);
      setOfflineSyncState((prev) => ({ ...prev, isProcessing: false }));

      // On error, update queue size to reflect actual state
      await updateQueueSize();
    } finally {
      isProcessingRef.current = false;
      syncedModulesRef.current.clear();
    }
  }, [isOnline, updateQueueSize, queryClient]);

  useEffect(() => {
    // Initial queue size fetch (non-blocking)
    updateQueueSize();

    const handleRequestRemoved = () => {
      setOfflineSyncState((prev) => ({
        ...prev,
        queueSize: Math.max(0, prev.queueSize - 1),
      }));
    };

    const handleRequestQueued = () => {
      setOfflineSyncState((prev) => ({
        ...prev,
        queueSize: prev.queueSize + 1,
      }));
    };

    const handleRequestProcessed = (
      event: CustomEvent<RequestProcessedEvent>,
    ) => {
      const { request, responseData } = event.detail;

      try {
        const { moduleKey, itemId } = parseRequestUrl(request.url);

        // Track which modules were synced for targeted invalidation
        if (moduleKey) {
          syncedModulesRef.current.add(moduleKey);
        }

        if (responseData && itemId && request.method !== "POST") {
          updateCacheForModule(
            queryClient,
            moduleKey,
            itemId,
            responseData as Record<string, unknown>,
            request.method,
          );
        } else if (moduleKey) {
          // Only invalidate the specific module
          queryClient.invalidateQueries({
            queryKey: [moduleKey],
          });
        }
      } catch (error) {
        console.error("Failed to process request for cache update:", error);
        // On parse error, invalidate the cache to ensure consistency
        queryClient.invalidateQueries();
      }
    };

    OfflineQueue.addEventListener("requestQueued", handleRequestQueued);
    OfflineQueue.addEventListener("requestRemoved", handleRequestRemoved);
    OfflineQueue.addEventListener(
      "requestProcessed",
      handleRequestProcessed as EventListener,
    );

    return () => {
      OfflineQueue.removeEventListener("requestQueued", handleRequestQueued);
      OfflineQueue.removeEventListener("requestRemoved", handleRequestRemoved);
      OfflineQueue.removeEventListener(
        "requestProcessed",
        handleRequestProcessed as EventListener,
      );
    };
  }, [updateQueueSize, queryClient]);

  return useMemo<OfflineSyncReturn>(() => {
    return {
      ...offlineSyncState,
      isOnline,
      processQueue,
    };
  }, [offlineSyncState, isOnline, processQueue]);
}
